import type React from "react";
import {
  type ChangeEvent,
  type KeyboardEvent,
  type SyntheticEvent,
  useEffect,
  useRef,
} from "react";
import { type IntlShape, useIntl } from "react-intl";
import { border_radius } from "src/design-system/tokens/border";
import XCircleSolid from "src/design-system/icons/XCircleSolid.svg?react";
import { useIsHotelsUrlDeeplink } from "src/utils/hooks/useNavigateToHotelsPage";
import { useForkRef } from "src/utils/useForkRef";
import styled, { css } from "styled-components";
import color from "src/design-system/tokens/color";
import { spacing } from "src/design-system/tokens/spacing";
import { font_size, font_weight } from "src/design-system/tokens/typography";
import messages from "./AutocompleteInput.messages";

type AutocompleteType = "origin" | "destination" | "hotelDestination" | number;

type Props = {
  type?: AutocompleteType;
  value: string;
  hint?: string;
  onChange: (query: string) => void;
  onFocus?: (e: SyntheticEvent) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  autoFocus?: boolean;
  id?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  darkHighlight?: boolean;
  solidBackground?: boolean;
  placeholder?: string;
  transparentBackground?: boolean;
  addDestinationRef?: React.RefObject<HTMLInputElement | null>;
  fullWidth?: boolean;
  curvedBorder?: boolean;
  noShadow?: boolean;
  noBorder?: boolean;
  isFocused?: boolean;
  showClearIcon?: boolean;
  handleClear?: () => void;
  horizontalSearch?: boolean;
  index?: number;
  role?: string;
  isDraggable?: boolean;
};

export function AutocompleteInput(props: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const intl = useIntl();
  const isHotelsScreen = useIsHotelsUrlDeeplink();
  const labelMsg = getAutocompleteInputLabelMsg(intl, props.type);
  const placeholderMsg = getAutocompleteInputPlaceHolderMsg(intl, props.type);

  const placeholderProps = props.placeholder ?? placeholderMsg;

  const id = props.id ?? "autocomplete";

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    props.onChange(e.currentTarget.value);
  }

  useEffect(() => {
    if (props.autoFocus && ref?.current) {
      ref.current.select();
    }
  }, [props.autoFocus, ref]);

  return (
    <SolidBackgroundWrapper
      className={props.className}
      disabled={props.disabled}
      fullWidth={props.fullWidth}
      noShadow={props.noShadow}
      noBorder={props.noBorder}
      isFocused={props.isFocused}
      hasIcon={!!props.icon}
      transparentBackground={props.transparentBackground}
      horizontalSearch={props.horizontalSearch}
      isDraggable={props.isDraggable}
      isHotelsScreen={isHotelsScreen}
      role={props.role}
    >
      {/** If showing minimal styled input or icon, don't show label */}
      {labelMsg && !props.transparentBackground && !props.icon && (
        <Label htmlFor={id}>{labelMsg}</Label>
      )}
      {props.icon}

      <InputHintWrapper $hint={props.hint ? `${props.value}${props.hint}` : ""}>
        <Input
          data-testid={`autocomplete-${props.type}`}
          id={id}
          ref={useForkRef(ref, props.addDestinationRef)}
          title={labelMsg ?? placeholderProps}
          value={props.value}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onChange={handleChange}
          onKeyDown={props.onKeyDown}
          disabled={props.disabled}
          // We can disable this autoFocus rule because we only ever have one instance
          // in the DOM at any one time.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={props.autoFocus}
          placeholder={placeholderProps}
          darkHighlight={props.darkHighlight}
          transparentBackground={props.transparentBackground}
          fullWidth={props.fullWidth}
          data-no-dnd="true"
          type="search"
          // Allow Hotjar to show destinations in input
          data-hj-allow="data-hj-allow"
        />
      </InputHintWrapper>

      {props.showClearIcon && props.handleClear && (
        <IconWrapper onClick={props.handleClear}>
          <XCircleSolid className="h-5 w-5 text-icon-secondary" />
        </IconWrapper>
      )}
    </SolidBackgroundWrapper>
  );
}

export function getAutocompleteInputLabelMsg(
  intl: IntlShape,
  type: AutocompleteType | undefined,
) {
  const labelMessages = {
    origin: intl.formatMessage(messages.origin),
    destination: intl.formatMessage(messages.destination),
  };

  return type === "origin" || type === "destination"
    ? labelMessages[type]
    : undefined;
}

function getAutocompleteInputPlaceHolderMsg(
  intl: IntlShape,
  type: AutocompleteType | undefined,
) {
  const placeholderMessages = {
    origin: intl.formatMessage(messages.originPlaceholder),
    destination: intl.formatMessage(messages.destinationPlaceholder),
  };
  return type === 0 || type === "origin"
    ? placeholderMessages.origin
    : placeholderMessages.destination;
}

const InputHintWrapper = styled.div<{
  $hint?: string;
}>`
  position: relative;
  width: 100%;
  ${(props) =>
    props.$hint &&
    css`
      &::before {
        top: 0;
        left: 0;
        position: absolute;
        content: "${props.$hint}";
        pointer-events: none;
        opacity: 0.5;
        flex: 1;
        white-space: nowrap;
      }
    `}
  input {
    width: 100%;
  }
`;

const SolidBackgroundWrapper = styled.div<{
  disabled?: boolean;
  fullWidth?: boolean;
  noShadow?: boolean;
  noBorder?: boolean;
  isFocused?: boolean;
  hasIcon?: boolean;
  transparentBackground?: boolean;
  horizontalSearch?: boolean;
  isDraggable?: boolean;
  isHotelsScreen?: boolean;
}>`
  display: flex;
  position: relative;
  align-items: center;
  border: 1px solid ${color.border.active};
  border-radius: ${border_radius.rounded_md};
  background-color: ${(props) =>
    props.transparentBackground ? "transparent" : color.bg.fill.solid};
  min-height: 44px;
  padding-left: 14px;

  & ${InputHintWrapper} {
    margin-left: ${spacing.sm};
  }

  ${({ horizontalSearch }) =>
    !horizontalSearch &&
    css`
      padding: ${spacing.lg};
      padding-left: 6px;
    `}

  ${({ horizontalSearch }) =>
    horizontalSearch &&
    css`
      & ${InputHintWrapper} {
        width: 194px;
        overflow: hidden;
        position: relative;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `}

  ${({ horizontalSearch, isDraggable }) =>
    horizontalSearch &&
    isDraggable &&
    css`
      & ${InputHintWrapper} {
        flex: unset;
        margin-left: ${spacing.sm};
      }
    `}

  ${({ noBorder }) =>
    !noBorder &&
    css`
      &:hover {
        border-color: ${color.border.selected};
      }
    `}

  ${({ hasIcon }) =>
    hasIcon &&
    css`
      & ${InputHintWrapper} {
        margin-left: ${spacing.lg};
      }
    `}

  ${({ disabled }) =>
    disabled && `background-color: ${color.bg.fill.solidHover}`}

  ${({ noShadow }) =>
    noShadow &&
    css`
      box-shadow: none;
    `}

  ${({ noBorder }) =>
    noBorder &&
    css`
      border: none;
    `}

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}

    ${({ isFocused, noBorder }) =>
    isFocused &&
    !noBorder &&
    css`
      border-color: ${color.border.selected};
    `}

    ${({ isHotelsScreen }) =>
    isHotelsScreen &&
    css`
      padding-left: ${spacing.lg};
    `}
`;

const Label = styled.label`
  text-transform: uppercase;
  color: ${color.bg.brand.fill};
  font-size: ${font_size.text_base};
  font-weight: ${font_weight.medium};
  padding-right: ${spacing.lg};
`;

const Input = styled.input<{
  darkHighlight?: boolean;
  transparentBackground?: boolean;
  fullWidth?: boolean;
}>`
  flex: 1 0 auto;
  border: none;
  font-size: ${font_size.text_base};
  color: ${color.text.primary};

  // Truncate long addresses in the input
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  background-color: ${(props) =>
    props.transparentBackground ? "transparent" : color.bg.fill.solid};

  &:focus {
    outline: none;
  }
  &:disabled {
    background-color: ${color.primitive.neutral300};
  }

  ${({ darkHighlight }) =>
    darkHighlight &&
    css`
      &::selection {
        background: ${color.primitive.neutral1500};
        color: ${color.bg.fill.solid};
      }
    `}

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}

    &::-webkit-search-cancel-button {
    display: none;
  }
`;

const IconWrapper = styled.button`
  position: relative;
  top: 2px;
  z-index: 999;

  &:hover {
    cursor: pointer;
  }
`;
